import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getAnchorSegmentCuts } from '@/lib/admin/insights-api';

function meanColor(v: number | null) {
  if (v == null) return 'hsl(var(--muted))';
  const t = Math.max(0, Math.min(1, (v - 1) / 4));
  const hue = 0 + t * 130; // red -> green
  return `hsl(${hue} 70% 45% / 0.85)`;
}

export function SegmentCutsTable({ surveyId }: { surveyId: string }) {
  const q = useQuery({
    queryKey: ['admin', 'segment-cuts', surveyId],
    queryFn: () => getAnchorSegmentCuts(surveyId),
  });

  const { anchors, bands, matrix } = useMemo(() => {
    const rows = q.data ?? [];
    const aMap = new Map<string, string>();
    const bMap = new Map<string, string>();
    const m = new Map<string, { mean: number | null; n: number; suppressed: boolean }>();
    for (const r of rows) {
      aMap.set(r.anchor_id, r.anchor_name);
      bMap.set(r.band_id, `${r.dimension}: ${r.band_label}`);
      m.set(`${r.anchor_id}|${r.band_id}`, {
        mean: r.mean,
        n: r.n,
        suppressed: r.suppressed,
      });
    }
    return {
      anchors: Array.from(aMap.entries()),
      bands: Array.from(bMap.entries()),
      matrix: m,
    };
  }, [q.data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-heading">Segment cuts by team</CardTitle>
        <CardDescription>
          Anchor means sliced by band. Cells with k&lt;5 are suppressed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : anchors.length === 0 || bands.length === 0 ? (
          <p className="text-sm text-muted-foreground">Add bands and responses to see segment cuts.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-card">Anchor</th>
                  {bands.map(([id, label]) => (
                    <th key={id} className="p-2 text-left whitespace-nowrap font-normal text-muted-foreground">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anchors.map(([aid, aname]) => (
                  <tr key={aid}>
                    <td className="p-2 font-medium sticky left-0 bg-card whitespace-nowrap">{aname}</td>
                    {bands.map(([bid]) => {
                      const cell = matrix.get(`${aid}|${bid}`);
                      if (!cell || cell.n === 0) {
                        return <td key={bid} className="p-2 text-muted-foreground">–</td>;
                      }
                      if (cell.suppressed) {
                        return (
                          <td key={bid} className="p-2 text-muted-foreground italic" title={`n=${cell.n} <5`}>
                            k&lt;5
                          </td>
                        );
                      }
                      return (
                        <td
                          key={bid}
                          className="p-2 text-white text-center rounded"
                          style={{ background: meanColor(cell.mean) }}
                          title={`n=${cell.n}`}
                        >
                          {cell.mean?.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
